/**
 * Выбор ortho/eemap/plain в меню подложек (задача владельца 24.09.2026).
 * Свой сигнал, а не state.ts: выбор нужен только меню подложек и карте
 * (terra-widgets.tsx, app.tsx, basemap-switch.ts), state.ts он не раздувает.
 */
import { signal } from '@preact/signals'

export const EXTRA_BASEMAPS = ['ortho', 'eemap', 'plain'] as const
export type ExtraBasemap = (typeof EXTRA_BASEMAPS)[number]

export function isExtraBasemap(v: string): v is ExtraBasemap {
  return (EXTRA_BASEMAPS as readonly string[]).includes(v)
}

/** bbox Эстонии с запасом — ortho/eemap показываем только рядом; «Схема» — везде. */
const ESTONIA_BBOX = [21.5, 57.3, 28.5, 59.95] as const

export function isNearEstonia(center: readonly [number, number]): boolean {
  const [lon, lat] = center
  return (
    lon >= ESTONIA_BBOX[0] &&
    lon <= ESTONIA_BBOX[2] &&
    lat >= ESTONIA_BBOX[1] &&
    lat <= ESTONIA_BBOX[3]
  )
}

function initialExtra(): ExtraBasemap | null {
  try {
    const saved = localStorage.getItem('basemap-extra')
    return saved && isExtraBasemap(saved) ? saved : null
  } catch {
    return null
  }
}

/** Текущий выбор ortho/eemap/plain; null — активна обычная подложка (state.ts). */
export const extraBasemap = signal<ExtraBasemap | null>(initialExtra())

export function setExtraBasemap(mode: ExtraBasemap | null): void {
  extraBasemap.value = mode
  try {
    if (mode) localStorage.setItem('basemap-extra', mode)
    else localStorage.removeItem('basemap-extra')
  } catch {
    // приватный режим — выбор просто не сохранится
  }
}

/**
 * Сохранённый выбор ortho/eemap/plain для открытой площадки. create() строит
 * стартовую подложку своей цепочкой (offline-style.ts), выбор применяется
 * следом. Maa-amet вне Эстонии не покрывает — такой выбор сбрасывается, иначе
 * меню ставило бы галочку у подложки, которой на карте нет. Без сети Maa-amet
 * не ответит: остаётся стартовая офлайн-подложка, выбор ждёт сети.
 */
export function extraBasemapFor(
  center: readonly [number, number] | undefined,
  online: boolean,
): ExtraBasemap | null {
  const m = extraBasemap.value
  if (!m) return null
  if (m !== 'plain' && !(center && isNearEstonia(center))) {
    setExtraBasemap(null)
    return null
  }
  return m === 'plain' || online ? m : null
}
